/**
 * Runs after Boost renders/refreshes the grid, reads each product's
 * variants (cached) to find the largest discount, and appends a saving badge.
 * Red styling requires custom.on_sale, custom.flash_sale, or custom.close_out on at least one variant.
 * The Sale badge requires custom.on_sale only. Flash Sale and Closeout follow the PDP rule
 * (flash sale wins over closeout) and are rendered as real badges so the card matches PDP/Quick View.
 */
(function () {
  'use strict';

  var productCache = {};
  var SALE_CLASS = 'boost-sd__product-item--sale-metafield';
  var HIGHLIGHT_CLASS = 'boost-sd__product-item--highlight-price';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchProduct(handle) {
    if (productCache[handle]) {
      return productCache[handle];
    }

    productCache[handle] = fetch(root() + 'products/' + handle + '?view=json-view')
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });

    return productCache[handle];
  }

  function readSaleFlags(product) {
    var variants = (product && product.variants) || [];
    var flags = { onSale: false, flashSale: false, closeout: false, highlight: false };

    for (var i = 0; i < variants.length; i++) {
      var metafields = variants[i].metafields;
      if (!metafields) {
        continue;
      }

      if (metafields.on_sale === true) {
        flags.onSale = true;
      }
      if (metafields.flash_sale) {
        flags.flashSale = true;
      }
      if (metafields.close_out) {
        flags.closeout = true;
      }
    }

    flags.highlight = flags.onSale || flags.flashSale || flags.closeout;

    return flags;
  }

  function computeSaving(product) {
    var maxDiscount = 0;
    var minDiscount = 100;
    var maxCompare = 0;
    var onSale = false;
    var variants = (product && product.variants) || [];

    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var compare = variant.compare_at_price || 0;

      if (compare > variant.price) {
        onSale = true;

        if (compare > maxCompare) {
          maxCompare = compare;
        }

        var discount = Math.round(((compare - variant.price) * 100) / compare);

        if (discount > maxDiscount) {
          maxDiscount = discount;
        }
        if (discount < minDiscount) {
          minDiscount = discount;
        }
      } else {
        minDiscount = 0;
      }
    }

    return {
      onSale: onSale,
      maxDiscount: maxDiscount,
      maxCompare: maxCompare,
      varies: minDiscount !== maxDiscount,
    };
  }

  function formatMoney(cents) {
    var hasCents = cents % 100 !== 0;
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    });
  }

  function getHandle(item) {
    var link = item.querySelector('a[href*="/products/"]');
    if (!link) {
      return null;
    }

    var match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function applySalePriceColor(item, highlightPrice) {
    var wrapper = item.querySelector('.boost-sd__product-price-wrapper');
    if (!wrapper) {
      return;
    }

    var saleColor = highlightPrice ? '#CD2424' : '#191919';
    var priceNodes = wrapper.querySelectorAll(
      '.boost-sd__product-price--sale, .boost-sd__product-price--default, .boost-sd__format-currency-separator, .boost-sd__product-price--sale .boost-sd__format-currency, .boost-sd__product-price--default .boost-sd__format-currency'
    );

    for (var i = 0; i < priceNodes.length; i++) {
      priceNodes[i].style.setProperty('color', saleColor, 'important');
    }
  }

  // Boost renders the tag badges itself, but only on cards that have at least one.
  function getBadgeContainer(item) {
    var container = item.querySelector('.badge-container');
    if (container) {
      return container;
    }

    var title = item.querySelector('.boost-sd__product-title');
    if (!title || !title.parentNode) {
      return null;
    }

    container = document.createElement('div');
    container.className = 'badge-container';
    title.parentNode.insertBefore(container, title);

    return container;
  }

  function setBadge(container, modifier, shouldShow, text) {
    var existing = container.querySelector('[data-plp-badge="' + modifier + '"]');

    if (!shouldShow) {
      if (existing) {
        existing.parentNode.removeChild(existing);
      }
      return;
    }

    if (existing) {
      return;
    }

    var badge = document.createElement('span');
    badge.className = 'badge badge--' + modifier;
    badge.setAttribute('data-plp-badge', modifier);
    badge.textContent = text;
    container.appendChild(badge);
  }

  function applyBadges(item, flags) {
    var container = getBadgeContainer(item);
    if (!container) {
      return;
    }

    setBadge(container, 'sale', flags.onSale, 'Sale');
    setBadge(container, 'flash-sale', flags.flashSale, 'Flash Sale');
    setBadge(container, 'closeout', flags.closeout && !flags.flashSale, 'Closeout');
  }

  function applyLabel(item, label, highlightPrice) {
    var wrapper = item.querySelector('.boost-sd__product-price-wrapper');
    if (!wrapper) {
      return;
    }

    var existing = wrapper.querySelector('.boost-sd__product-price--saving');

    if (existing) {
      if (existing.textContent.trim() !== label) {
        existing.textContent = label;
      }
      existing.classList.toggle('saving-badge--sale', highlightPrice);
      return;
    }

    var badge = document.createElement('span');
    badge.className =
      'boost-sd__product-price--saving' + (highlightPrice ? ' saving-badge--sale' : '');
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = label;
    wrapper.appendChild(badge);
  }

  function applyComparePrice(item, maxCompare) {
    var compareEl = item.querySelector('.boost-sd__format-currency--price-compare');
    if (!compareEl) {
      return;
    }

    var target = compareEl.querySelector('.boost-sd__format-currency') || compareEl;
    var formatted = formatMoney(maxCompare);

    if (target.textContent.trim() !== formatted) {
      target.textContent = formatted;
    }
  }

  function processItem(item) {
    if (!item.querySelector('.boost-sd__product-price--sale')) {
      return;
    }

    var handle = getHandle(item);
    if (!handle) {
      return;
    }

    fetchProduct(handle).then(function (product) {
      if (!product) {
        return;
      }

      var flags = readSaleFlags(product);
      var highlightPrice = flags.highlight;
      item.classList.toggle(SALE_CLASS, flags.onSale);
      item.classList.toggle(HIGHLIGHT_CLASS, highlightPrice);
      applySalePriceColor(item, highlightPrice);
      applyBadges(item, flags);

      var info = computeSaving(product);

      if (!info.onSale || info.maxDiscount <= 0) {
        return;
      }

      var label = (info.varies ? 'Up to ' : '') + info.maxDiscount + '% OFF';
      applyLabel(item, label, highlightPrice);

      if (info.maxCompare > 0) {
        applyComparePrice(item, info.maxCompare);
      }
    });
  }

  function scan() {
    var items = document.querySelectorAll('.boost-sd__product-item');
    for (var i = 0; i < items.length; i++) {
      processItem(items[i]);
    }
  }

  function init() {
    scan();

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) {
        return;
      }
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        scan();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('load', scan);
    setTimeout(scan, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
